<template>
  <div>
    <table class="w-full rounded">
      <caption class="text-left">
        Purchase summary
      </caption>
      <thead class="text-xs">
        <tr>
          <th class="text-left h-7">
            <span class="text-xs text-fg-disclaimer">Description</span>
          </th>
          <th class="text-right h-7 px-4">
            <span class="text-xs text-fg-disclaimer">
              {{ addOns && addOns.length > 0 ? 'Qty' : 'Tickets' }}
            </span>
          </th>
          <th class="text-right h-7 px-4">
            <span class="text-xs text-fg-disclaimer">Each</span>
          </th>
          <th class="text-right h-7">
            <span class="text-xs text-fg-disclaimer">Total</span>
          </th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="(table, index) in tablesFromTransactions"
          :key="index"
          :id="`table-line-${table.id}`"
          class="text-sm text-fg-g-primary"
        >
          <td :id="`table-line-${table.id}-description`" class="h-7">Table ({{ table.name }})</td>
          <td :id="`table-line-${table.id}-tickets`" class="text-right h-7 px-4">
            {{ table.guests }}
          </td>
          <td :id="`table-line-${table.id}-each`" class="text-right h-7 px-4">
            {{ currency(table.price) }}
          </td>
          <td :id="`table-line-${table.id}-total`" class="text-right h-7">
            {{ currency(table.guests * +table.price) }}
          </td>
        </tr>
        <template v-if="!!addOns">
          <tr
            v-for="addOn in addOnsInTable"
            :key="addOn.id"
            :id="`addOn-line-${addOn.id}`"
            class="text-sm text-fg-g-primary"
          >
            <td :id="`addOn-line-${addOn.id}-name`" class="h-7">{{ addOn.name }}</td>
            <td :id="`addOn-line-${addOn.id}-quantity`" class="text-right h-7 px-4">
              {{ addOn.quantity }}
            </td>
            <td :id="`addOn-line-${addOn.id}-price`" class="text-right h-7 px-4">
              {{ currency(addOn.price) }}
            </td>
            <td :id="`addOn-line-${addOn.id}-price-total`" class="text-right h-7">
              {{ currency(addOn.quantity * addOn.price) }}
            </td>
          </tr>
        </template>
      </tbody>
    </table>
    <div class="tax-information flex justify-between text-sm mt-1.5 group">
      <div class="flex items-baseline space-x-2">
        <span>Taxes &amp; Fees</span>
        <TooltipTrigger
          label="Taxes and fees breakdown"
          tooltip-id="order-confirmation-tax-fees-tooltip"
        >
          <div class="flex flex-col space-y-1">
            <div class="flex justify-between items-baseline">
              Taxes <span>{{ currency(calculatedTax) }}</span>
            </div>
            <div class="flex justify-between items-baseline">
              Processing Fees <span>{{ currency(serviceFeeBeforeDiscount) }}</span>
            </div>
          </div>
        </TooltipTrigger>
      </div>

      <div id="taxes-and-fees-total" class="text-right">{{ taxesAndFees }}</div>
    </div>
    <div
      v-if="orderConfirmationStore.reservation && isDiscounted"
      class="flex justify-between text-sm mt-1.5"
      id="promo-discount"
    >
      <div class="peer flex items-baseline space-x-2">Promotional Discount</div>
      <div id="promo-discount-amount" class="text-right">-{{ currency(calculatedDiscounted) }}</div>
    </div>
    <hr class="bg-base-secondary w-full my-2" />
    <div class="flex items-baseline justify-between">
      <span class="">Total</span>
      <span id="total-price" class="font-semibold">{{ currency(calculatedTotal) }}</span>
    </div>
  </div>
</template>

<script lang="ts">
import { mapState, mapStores } from 'pinia'
import { defineComponent } from 'vue'

import TooltipTrigger from '@/common/components/molecules/TooltipTrigger.vue'
import useOrderConfirmationStore from '@/modules/guestUi/stores/OrderConfirmationStore'
import { EnrichedAddOn, serviceFeeBeforeDiscount } from '@/orders'
import { useSettingStore } from '@/stores/setting'
import { sortBy, sum } from '@/utils/utils'

import { ReservationAddOnWrite } from '@generated/types'

interface TransactionTable {
  id: number
  name: string
  price: string | number
  guests: number
}

export default defineComponent({
  name: 'CheckoutDetailTable',
  components: {
    TooltipTrigger,
  },
  computed: {
    ...mapStores(useOrderConfirmationStore, useSettingStore),
    ...mapState(useSettingStore, ['currency']),
    addOns(): ReservationAddOnWrite[] | null {
      return this.orderConfirmationStore.add_ons
    },
    addOnsInTable(): EnrichedAddOn[] {
      return sortBy(this.orderConfirmationStore.addOnsModels || [], 'name')
    },
    tablesFromTransactions(): TransactionTable[] {
      const reservation = this.orderConfirmationStore.reservation
      const performanceTables = this.orderConfirmationStore.performance?.tables
      if (!reservation?.res_tables || reservation.res_tables.length === 0) return []
      if (!performanceTables) return []

      return reservation.res_tables.map(resTable => {
        const performanceTable = performanceTables.find(
          table => table.id === resTable.performancetable_id,
        )
        return {
          id: resTable.performancetable_id,
          name: performanceTable?.name || '',
          price: resTable.price_per_person || 0,
          guests: resTable.guests || 0,
        }
      })
    },
    nonRefundedTransactions() {
      const reservation = this.orderConfirmationStore.reservation
      if (!reservation?.transactions) return []
      return reservation.transactions.filter(transaction => Number(transaction.refunded || 0) <= 0)
    },
    calculatedTax(): number {
      return sum(this.nonRefundedTransactions.map(t => Number(t.tax || 0)))
    },
    calculatedServiceFee(): number {
      return sum(this.nonRefundedTransactions.map(t => Number(t.service_fee || 0)))
    },
    calculatedDiscounted(): number {
      return sum(this.nonRefundedTransactions.map(t => Number(t.discounted || 0)))
    },
    ticketsBeforeDiscount(): number {
      return sum(
        this.tablesFromTransactions.map(table => Number(table.price) * Number(table.guests || 0)),
      )
    },
    serviceFeeBeforeDiscount(): number {
      return serviceFeeBeforeDiscount({
        serviceFee: this.calculatedServiceFee,
        discounted: this.calculatedDiscounted,
        ticketsBeforeDiscount: this.ticketsBeforeDiscount,
        ticketsAfterDiscount: sum(this.nonRefundedTransactions.map(t => Number(t.amount || 0))),
      })
    },
    calculatedTotal(): number {
      const addOnsSum = sum(this.addOnsInTable.map(addOn => addOn.quantity * addOn.price))

      return (
        this.ticketsBeforeDiscount +
        addOnsSum +
        this.calculatedTax +
        this.serviceFeeBeforeDiscount -
        this.calculatedDiscounted
      )
    },
    taxesAndFees(): string {
      return this.currency(this.calculatedTax + this.serviceFeeBeforeDiscount)
    },
    isDiscounted(): boolean {
      return this.calculatedDiscounted > 0
    },
  },
})
</script>
